import React, {useState, useEffect, useRef} from "react"
import {capitalizeFirstLetter} from "../../../services/HelperService"
import {ReactComponent as PenBorderSvg} from "../../../assets/svg/pen-border.svg"
import {ReactComponent as PlusSvg} from "../../../assets/svg/plus.svg"
import ModalWidget from "../ModalWidget"
import {useParams} from "react-router-dom";
import SelectField from "../../SelectField";
import SubjectNameMultiSelect from "../../selects/SubjectNameMultiSelect";
import {MenuOption} from "../../../models/MenuOption";
import {addSubjectToClassroom, getClassroomById} from "../../../services/ClassroomService";
import {getSubjectSuggestions} from "../../../services/SchoolService";
import {listSubjects} from "../../../services/GradebookService";
import {AccountType} from "../../../models/AccountType";
import {useMenu} from "../../../contexts/MenuContext";
import {createUserLoadOptions, formatTeacherOption} from "../../../utils/selectHelpers";
import CheckboxWidget from "../../CheckboxWidget";
import {useClassroom} from "../../../contexts/ClassroomContext";
import SubjectTagMultiSelect from "../../reports/SubjectTagMultiSelect";

interface AddSubjectProps {
    show?: boolean;
    onHide?: () => void;
}

interface SelectOption {
    value: string;
    label: string;
}

function AddSubject({show = true}: AddSubjectProps) {
    const [selectedSubjects, setSelectedSubjects] = useState<SelectOption[] | null>(null);
    const [, setSelectedTeachers] = useState<SelectOption[] | null>(null);
    const [showTeacher, setShowTeacher] = useState(false);
    const {navigateTo} = useMenu()
    const {updateClassroom, setPendingSubjectSelection} = useClassroom()
    const {id} = useParams()
    const singularTerm = new MenuOption(MenuOption.SUBJECTS).getSingularWord().toLowerCase()
    const capitalWord = capitalizeFirstLetter(singularTerm) + '(s)'
    const [subjectOptions, setSubjectOptions] = useState<SelectOption[]>([])
    const [subjectsLoading, setSubjectsLoading] = useState(true)
    const [duplicateWarnings, setDuplicateWarnings] = useState<string[]>([])
    const [sharedTags, setSharedTags] = useState<string[] | null>(null)
    const [saveState, setSaveState] = useState<'idle' | 'saving' | 'completed'>('idle')
    const completedTimerRef = useRef<NodeJS.Timeout | null>(null)

    useEffect(() => {
        return () => {
            if (completedTimerRef.current) clearTimeout(completedTimerRef.current);
        };
    }, []);

    const assign = async () => {
        try {
            const subjects = selectedSubjects?.map((subject) => ({
                name: (subject.value && subject.label)? subject.label: subject.value,
            }));

            if ((selectedSubjects?.length ?? 0) === 0) {
                return;
            }

            // Client-side dedup: keep only the first occurrence of each name
            const seen = new Set<string>();
            const uniqueSubjects = (subjects ?? []).filter((s) => {
                const key = s.name.toLowerCase();
                if (seen.has(key)) return false;
                seen.add(key);
                return true;
            });

            setSaveState('saving');

            const skipped: string[] = [];
            let lastCreatedSubjectId: string | null = null;
            let lastCreatedSubjectName: string | undefined;
            for (const subject of uniqueSubjects) {
                try {
                    const created = await addSubjectToClassroom(subject.name, id!, sharedTags);
                    const createdId = created?.id ?? created?.uuid;
                    if (createdId) {
                        lastCreatedSubjectId = createdId;
                        lastCreatedSubjectName = created?.name ?? subject.name;
                    }
                } catch (err: any) {
                    if (err?.response?.status === 409) {
                        skipped.push(subject.name);
                        continue;
                    }
                    throw err;
                }
            }
            if (skipped.length > 0) {
                setDuplicateWarnings(skipped);
                if (skipped.length === uniqueSubjects.length) {
                    setSaveState('idle');
                    return;
                }
            }

            if (id) {
                try {
                    const refreshedClassroom = await getClassroomById(id)
                    updateClassroom(refreshedClassroom as any)
                } catch {
                    // If the refresh request fails, still let the modal close after a successful save.
                }
            }

            if (id && lastCreatedSubjectId) {
                setPendingSubjectSelection({
                    classroomId: id,
                    subjectId: lastCreatedSubjectId,
                    name: lastCreatedSubjectName,
                });
            }

            setSaveState('completed');
            completedTimerRef.current = setTimeout(() => {
                navigateTo(-1, false);
            }, 1500);
        } catch (err) {
            setSaveState('idle');
        }
    };

    const getSubjects = async () => {
        setSubjectsLoading(true);
        try {
            const [subjectSuggestions, classroomSubjects] = await Promise.all([
                getSubjectSuggestions(undefined, 100),
                id ? listSubjects(id) : Promise.resolve([]),
            ]);

            const existingNames = new Set(
                classroomSubjects.map((s: any) => s.name.toLowerCase())
            );

            const seenSuggestionNames = new Set<string>();

            setSubjectOptions(
                subjectSuggestions
                    .filter((subject: any) => {
                        const normalizedName = subject.name.toLowerCase();
                        if (existingNames.has(normalizedName) || seenSuggestionNames.has(normalizedName)) {
                            return false;
                        }
                        seenSuggestionNames.add(normalizedName);
                        return true;
                    })
                    .map((subject: any) => ({
                        value: subject.name,
                        label: subject.name,
                    }))
            );
        }
        catch(err)
        {
            //console.log(err)
        }
        finally {
            setSubjectsLoading(false);
        }
    }
    const getTeachers = async ()=>{
        // Options now loaded via async SelectField
    }

    useEffect(()=>{
        getSubjects()
        getTeachers()
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    return (
        <>
            {
                <ModalWidget
                    headerIcon={PenBorderSvg}
                    show={show}
                    title={'Add ' + capitalWord}
                    description={'You can select more than one ' + singularTerm + ' at a time.'}
                    actionTitle={'Add ' + capitalWord + ' to Class'}
                    actionIcon={PlusSvg}
                    onAction={assign}
                    actionLoading={saveState === 'saving'}
                    actionCompleted={saveState === 'completed'}
                    actionDisabled={!selectedSubjects || selectedSubjects.length === 0}
                >
                    <div>
                        <label className="form-label font-sm font-bold-light mb-1 d-block">
                            {capitalWord} Name or ID
                        </label>
                        <SubjectNameMultiSelect
                            options={subjectOptions}
                            value={selectedSubjects}
                            onChange={setSelectedSubjects}
                            placeholder="English Language"
                            isLoading={subjectsLoading}
                        />
                        <div className="font-xs font-bold-light mt-2">
                            {'Number of ' + singularTerm + '(s) selected: ' + (selectedSubjects?.length ?? 0)}
                        </div>
                    </div>

                    <div className="mt-3">
                        <label className="form-label font-sm font-bold-light mb-1">
                            Section tags (optional)
                        </label>
                        <SubjectTagMultiSelect
                            value={sharedTags}
                            onChange={setSharedTags}
                            placeholder="Add tags, e.g. Sciences, Languages"
                        />
                        <div className="font-xs text-muted mt-1">
                            Applied to every subject added in this batch. Tags group subjects on report cards.
                        </div>
                    </div>

                    {duplicateWarnings.length > 0 && (
                        <div className="mt-2 font-xs" style={{ color: '#dc3545' }}>
                            Already assigned: {duplicateWarnings.join(', ')}
                        </div>
                    )}

                    <CheckboxWidget
                        className="mt-3"
                        label="Assign Teacher(s)"
                        isChecked={showTeacher}
                        onValueChange={(checked) => {
                            setShowTeacher(checked);
                            if (!checked) setSelectedTeachers(null);
                        }}
                    />

                    {showTeacher && (
                        <SelectField className="mt-3" label={'Teacher(s) Name or ID'}
                                     loadOptions={createUserLoadOptions(AccountType.TEACHER)}
                                     formatOptionLabel={formatTeacherOption}
                                     fieldType={AccountType.TEACHER}
                                     placeholder="John Doe" onValueChange={setSelectedTeachers}/>
                    )}
                </ModalWidget>
            }
        </>
    )
}

export default AddSubject
